import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PhoneIcon, SunIcon } from '@heroicons/react/24/outline';

import { useAuth } from '../../context/AuthContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.header 
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/80 backdrop-blur-md shadow-[0_4px_30px_rgba(0,0,0,0.03)] border-b border-gray-100 py-3' 
          : 'bg-transparent py-5'
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 lg:px-8" aria-label="Global">
        
        {/* Left: Logo & Branding */}
        <div className="flex items-center gap-x-4 flex-1">
          <Link to="/" className="-m-1.5 p-1.5 flex items-center group">
            <div className="flex items-center justify-center bg-[#dc343b] text-white p-1.5 rounded-lg mr-3 shadow-md group-hover:scale-105 transition-transform">
              <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='currentColor'>
                <path d='M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z'/>
              </svg>
            </div>
            <div className="flex items-center">
              <span className="text-gray-300 mx-2 text-xl font-light">|</span>
              <div className="flex flex-col">
                <span className="text-lg font-extrabold tracking-tight text-[#0f172a] leading-none">
                  NetrootX
                </span>
                <span className="text-[9px] font-bold tracking-[0.2em] text-gray-400 mt-1 uppercase">
                  Digital • Ledger • Secure
                </span>
              </div>
            </div>
          </Link>
        </div>

        {/* Center: Navigation Links */}
        <div className="hidden lg:flex lg:gap-x-10">
          {[
            { name: 'Home', href: '/' },
            { name: 'Features', href: '#features' },
            { name: 'Pricing', href: '#pricing' },
            { name: 'About', href: '#about' }
          ].map((item) => (
            <a 
              key={item.name} 
              href={item.href} 
              className="text-[14px] font-semibold leading-6 text-gray-600 hover:text-[#dc343b] transition-colors relative group"
            >
              {item.name}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#dc343b] transition-all group-hover:w-full"></span>
            </a>
          ))}
        </div>

        {/* Right: CTA & Theme Toggle */}
        <div className="flex flex-1 justify-end items-center gap-x-6">
          <span className="hidden lg:block text-gray-200 font-light">|</span>
          {user ? (
            <Link
              to="/dashboard"
              className="hidden lg:flex items-center gap-x-2 rounded-full bg-[#dc343b] px-5 py-2.5 text-[13px] font-bold text-white shadow-sm hover:bg-[#c42a31] transition-all hover:-translate-y-0.5"
            >
              Go to Dashboard
            </Link>
          ) : (
            <Link
              to="/login"
              className="hidden lg:flex items-center gap-x-2 rounded-full bg-[#0f172a] px-5 py-2.5 text-[13px] font-semibold text-white shadow-sm hover:bg-gray-800 transition-all hover:-translate-y-0.5"
            >
              Log in
            </Link>
          )}
          <button className="text-gray-400 hover:text-gray-900 transition-colors">
            <SunIcon className="h-5 w-5" />
          </button>
        </div>
      </nav>
    </motion.header>
  );
};

export default Header;
